import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Users, Plus, KeyRound, Pencil, Ban, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/Page";

const EMP_TYPE = { festangestellt: "Festangestellt", haushilfe: "Aushilfe" };
const ROLE = { admin: "Administrator", employee: "Mitarbeiter", super_admin: "Super-Admin" };
const NEW = { username: "", name: "", password: "", role: "employee", employee_type: "festangestellt", weekly_hours: "", notify_email: "", welcome_message: "", department_ids: [] };

export default function Mitarbeiter() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [company, setCompany] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(NEW);
  const [resetFor, setResetFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [resendFor, setResendFor] = useState(null);
  const [resendEmail, setResendEmail] = useState("");
  const [resendWelcome, setResendWelcome] = useState("");

  const load = () => api.get("/employees").then((r) => setRows(r.data || []));
  useEffect(() => {
    load();
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
    api.get("/departments").then((r) => setDepartments(r.data || [])).catch(() => {});
  }, []);

  const deptName = (id) => departments.find((d) => d.id === id)?.name;

  const create = async () => {
    try {
      const payload = { ...form, weekly_hours: form.weekly_hours ? Number(form.weekly_hours) : null };
      const r = await api.post("/employees", payload);
      toast.success("Mitarbeiter angelegt");
      if (form.notify_email) {
        if (r.data?.notify?.notified) toast.success(`Zugangsdaten an ${form.notify_email} gesendet`);
        else if (r.data?.notify?.reason === "demo") toast.info("E-Mail-Versand ist in der Demo deaktiviert");
        else toast.error("Zugangsdaten konnten nicht per E-Mail gesendet werden");
      }
      setOpen(false);
      setForm(NEW);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doReset = async () => {
    try {
      await api.post(`/employees/${resetFor.user_id}/reset-password`, { new_password: newPw });
      toast.success(`Passwort für ${resetFor.name} zurückgesetzt`);
      setResetFor(null);
      setNewPw("");
    } catch (e) { toast.error(apiError(e)); }
  };

  const openResend = (u) => {
    setResendEmail(u.email || "");
    setResendWelcome(company?.welcome_message || "");
    setResendFor(u);
  };

  const doResend = async () => {
    try {
      const r = await api.post(`/employees/${resendFor.user_id}/resend-credentials`, { to_email: resendEmail, welcome_message: resendWelcome });
      if (r.data?.notified) toast.success(`Neue Zugangsdaten an ${r.data.email || resendEmail} gesendet`);
      else if (r.data?.reason === "demo") toast.info("E-Mail-Versand ist in der Demo deaktiviert");
      else toast.error("E-Mail konnte nicht gesendet werden");
      setResendFor(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = (u) => {
    setEditForm({
      name: u.name || "", role: u.role === "admin" ? "admin" : "employee",
      employee_type: u.employee_type || "festangestellt",
      weekly_hours: u.weekly_hours ?? "", hourly_wage: u.hourly_wage ?? "", vacation_days_total: u.vacation_days_total ?? "",
      email: u.email || "", phone: u.phone || "", address: u.address || "",
      birth_date: u.birth_date || "", start_date: u.start_date || "",
      department_ids: u.department_ids || [],
      cost_rate: u.cost_rate ?? "", sell_rate: u.sell_rate ?? "",
    });
    setEditFor(u);
  };

  const saveEdit = async () => {
    try {
      const payload = {
        ...editForm,
        weekly_hours: editForm.weekly_hours === "" ? null : Number(editForm.weekly_hours),
        hourly_wage: editForm.hourly_wage === "" ? null : Number(editForm.hourly_wage),
        vacation_days_total: editForm.vacation_days_total === "" ? null : Number(editForm.vacation_days_total),
        cost_rate: editForm.cost_rate === "" ? null : Number(editForm.cost_rate),
        sell_rate: editForm.sell_rate === "" ? null : Number(editForm.sell_rate),
      };
      await api.put(`/employees/${editFor.user_id}`, payload);
      toast.success("Änderungen gespeichert");
      setEditFor(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleStatus = async (u) => {
    const next = u.active === false;
    try {
      await api.post(`/employees/${u.user_id}/status`, { active: next });
      toast.success(next ? `${u.name} aktiviert` : `${u.name} deaktiviert`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Mitarbeiter"
        subtitle="Team anlegen, bearbeiten, sperren und Passwörter zurücksetzen."
        testid="mitarbeiter-header"
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); setForm(v ? { ...NEW, welcome_message: company?.welcome_message || "" } : NEW); }}>
            <DialogTrigger asChild><Button data-testid="employee-create-open" className="brand-bg text-white hover:opacity-90"><Plus className="h-4 w-4 mr-2" /> Mitarbeiter</Button></DialogTrigger>
            <DialogContent data-testid="employee-create-dialog">
              <DialogHeader><DialogTitle>Neuer Mitarbeiter</DialogTitle><DialogDescription>Lege ein neues Teammitglied mit Zugangsdaten an.</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Name</Label><Input data-testid="employee-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Benutzername</Label><Input data-testid="employee-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="klein, eindeutig" /></div>
                </div>
                <div className="space-y-2"><Label>Passwort</Label><Input data-testid="employee-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="mind. 4 Zeichen" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>Rolle</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="employee-role"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="employee">Mitarbeiter</SelectItem><SelectItem value="admin">Administrator</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Anstellung</Label>
                    <Select value={form.employee_type} onValueChange={(v) => setForm({ ...form, employee_type: v })}>
                      <SelectTrigger data-testid="employee-type"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="festangestellt">Festangestellt</SelectItem><SelectItem value="haushilfe">Aushilfe</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Wochenstd.</Label><Input type="number" data-testid="employee-hours" value={form.weekly_hours} onChange={(e) => setForm({ ...form, weekly_hours: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Zugangsdaten per E-Mail senden (optional)</Label><Input data-testid="employee-notify" value={form.notify_email} onChange={(e) => setForm({ ...form, notify_email: e.target.value })} placeholder="mitarbeiter@firma.de – Link, Benutzername & Passwort" /></div>
                {form.notify_email && (
                  <div className="space-y-2"><Label>Willkommens-Text (optional, überschreibt Standard)</Label><Textarea data-testid="employee-welcome" value={form.welcome_message} onChange={(e) => setForm({ ...form, welcome_message: e.target.value })} placeholder="Herzlich willkommen im Team!" /></div>
                )}
                {departments.length > 0 && (
                  <div className="space-y-2">
                    <Label>Abteilungen</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {departments.map((d) => {
                        const checked = (form.department_ids || []).includes(d.id);
                        return (
                          <label key={d.id} className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer" data-testid={`create-dept-${d.id}`}>
                            <Checkbox checked={checked} onCheckedChange={(v) => {
                              const cur = form.department_ids || [];
                              setForm({ ...form, department_ids: v ? [...cur, d.id] : cur.filter((x) => x !== d.id) });
                            }} />
                            <span className="text-sm text-slate-700">{d.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter><Button onClick={create} data-testid="employee-create-submit" className="brand-bg text-white hover:opacity-90">Anlegen</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <Card className="border-slate-200 rounded-xl overflow-hidden">
        {rows.length === 0 ? (
          <EmptyState icon={Users} title="Keine Mitarbeiter" testid="mitarbeiter-empty" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Benutzername</TableHead>
                <TableHead>Rolle</TableHead>
                <TableHead>Anstellung</TableHead>
                <TableHead>Wochenstunden</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((u) => {
                const inactive = u.active === false;
                const isSuper = u.role === "super_admin";
                const isSelf = u.user_id === me?.user_id;
                return (
                  <TableRow key={u.user_id} data-testid={`employee-row-${u.user_id}`} className={inactive ? "opacity-50" : ""}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full brand-bg text-white flex items-center justify-center text-xs font-bold">{u.name?.charAt(0)}</div>
                        <div>
                          <div>{u.name}</div>
                          {(u.department_ids || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {(u.department_ids || []).map((id) => deptName(id) && (
                                <Badge key={id} variant="outline" className="bg-slate-50 text-[10px] px-1.5 py-0">{deptName(id)}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{u.username}</TableCell>
                    <TableCell><Badge variant="outline" className="bg-slate-50">{ROLE[u.role] || u.role}</Badge></TableCell>
                    <TableCell>{EMP_TYPE[u.employee_type] || "–"}</TableCell>
                    <TableCell>{u.weekly_hours ? `${u.weekly_hours} h` : "–"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={inactive ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}>
                        {inactive ? "Deaktiviert" : "Aktiv"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {!isSuper && !isSelf && (
                        <div className="flex items-center justify-end gap-1.5">
                          <Button variant="outline" size="sm" onClick={() => openEdit(u)} data-testid={`employee-edit-${u.user_id}`}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="outline" size="sm" onClick={() => openResend(u)} data-testid={`employee-resend-${u.user_id}`} title="Zugangsdaten erneut senden"><Mail className="h-4 w-4" /></Button>
                          <Button variant="outline" size="sm" onClick={() => { setResetFor(u); setNewPw(""); }} data-testid={`employee-reset-${u.user_id}`}><KeyRound className="h-4 w-4" /></Button>
                          {inactive ? (
                            <Button variant="outline" size="sm" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => toggleStatus(u)} data-testid={`employee-activate-${u.user_id}`}><CheckCircle2 className="h-4 w-4" /></Button>
                          ) : (
                            <Button variant="outline" size="sm" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => toggleStatus(u)} data-testid={`employee-deactivate-${u.user_id}`}><Ban className="h-4 w-4" /></Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editFor} onOpenChange={(v) => !v && setEditFor(null)}>
        <DialogContent data-testid="employee-edit-dialog">
          <DialogHeader><DialogTitle>Mitarbeiter bearbeiten{editFor ? ` – ${editFor.name}` : ""}</DialogTitle><DialogDescription>Stammdaten und Kontaktangaben ändern.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Name</Label><Input data-testid="edit-name" value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
              <div className="space-y-2">
                <Label>Rolle</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger data-testid="edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="employee">Mitarbeiter</SelectItem><SelectItem value="admin">Administrator</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Anstellung</Label>
                <Select value={editForm.employee_type} onValueChange={(v) => setEditForm({ ...editForm, employee_type: v })}>
                  <SelectTrigger data-testid="edit-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="festangestellt">Festangestellt</SelectItem><SelectItem value="haushilfe">Aushilfe</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Wochenstunden</Label><Input type="number" data-testid="edit-hours" value={editForm.weekly_hours} onChange={(e) => setEditForm({ ...editForm, weekly_hours: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>E-Mail</Label><Input data-testid="edit-email" value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
              <div className="space-y-2"><Label>Telefon</Label><Input data-testid="edit-phone" value={editForm.phone || ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Adresse</Label><Input data-testid="edit-address" value={editForm.address || ""} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Urlaubstage / Jahr</Label><Input type="number" data-testid="edit-vacation-days" value={editForm.vacation_days_total} onChange={(e) => setEditForm({ ...editForm, vacation_days_total: e.target.value })} /></div>
              <div className="space-y-2"><Label>Stundenlohn (€)</Label><Input type="number" step="0.01" data-testid="edit-hourly-wage" value={editForm.hourly_wage} onChange={(e) => setEditForm({ ...editForm, hourly_wage: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Geburtsdatum</Label><Input type="date" data-testid="edit-birth-date" value={editForm.birth_date || ""} onChange={(e) => setEditForm({ ...editForm, birth_date: e.target.value })} /></div>
              <div className="space-y-2"><Label>Eintrittsdatum</Label><Input type="date" data-testid="edit-start-date" value={editForm.start_date || ""} onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })} /></div>
            </div>
            <div className="space-y-2">
              <Label>Abteilungen</Label>
              {departments.length === 0 ? (
                <p className="text-xs text-slate-400">Noch keine Abteilungen angelegt. Lege sie unter „Abteilungen" an.</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {departments.map((d) => {
                    const checked = (editForm.department_ids || []).includes(d.id);
                    return (
                      <label key={d.id} className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer" data-testid={`edit-dept-${d.id}`}>
                        <Checkbox checked={checked} onCheckedChange={(v) => {
                          const cur = editForm.department_ids || [];
                          setEditForm({ ...editForm, department_ids: v ? [...cur, d.id] : cur.filter((x) => x !== d.id) });
                        }} />
                        <span className="text-sm text-slate-700">{d.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            {(company?.features || []).includes("margin") && (
              <div className="space-y-2 pt-3 border-t border-slate-100">
                <Label>Projektsätze (Margin-Report – für Mitarbeiter nicht sichtbar)</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label className="text-xs text-slate-500">Kostensatz (€/h)</Label><Input type="number" step="0.01" data-testid="edit-cost-rate" value={editForm.cost_rate} onChange={(e) => setEditForm({ ...editForm, cost_rate: e.target.value })} placeholder="z. B. 28" /></div>
                  <div className="space-y-2"><Label className="text-xs text-slate-500">Verkaufssatz (€/h)</Label><Input type="number" step="0.01" data-testid="edit-sell-rate" value={editForm.sell_rate} onChange={(e) => setEditForm({ ...editForm, sell_rate: e.target.value })} placeholder="z. B. 55" /></div>
                </div>
                <p className="text-xs text-slate-400">Werden bei Selbstbuchungen des Mitarbeiters automatisch angewandt.</p>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={saveEdit} data-testid="edit-submit" className="brand-bg text-white hover:opacity-90">Speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend credentials dialog */}
      <Dialog open={!!resendFor} onOpenChange={(v) => !v && setResendFor(null)}>
        <DialogContent data-testid="employee-resend-dialog">
          <DialogHeader><DialogTitle>Zugangsdaten erneut senden{resendFor ? ` – ${resendFor.name}` : ""}</DialogTitle><DialogDescription>Erzeugt ein neues Passwort und sendet Portal-Link, Benutzername & Passwort per E-Mail.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>E-Mail-Adresse</Label><Input data-testid="resend-email" value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} placeholder="mitarbeiter@firma.de" /></div>
            <div className="space-y-2"><Label>Willkommens-Text (optional, überschreibt Standard)</Label><Textarea data-testid="resend-welcome" value={resendWelcome} onChange={(e) => setResendWelcome(e.target.value)} placeholder="Herzlich willkommen im Team!" /></div>
          </div>
          <DialogFooter><Button onClick={doResend} data-testid="resend-submit" className="brand-bg text-white hover:opacity-90">Neues Passwort senden</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetFor} onOpenChange={(v) => !v && setResetFor(null)}>
        <DialogContent data-testid="employee-reset-dialog">
          <DialogHeader><DialogTitle>Passwort zurücksetzen{resetFor ? ` – ${resetFor.name}` : ""}</DialogTitle><DialogDescription>Vergib ein neues Passwort für diesen Zugang.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            <Label>Neues Passwort</Label>
            <Input data-testid="reset-new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="mind. 4 Zeichen" />
          </div>
          <DialogFooter><Button onClick={doReset} data-testid="reset-submit" className="brand-bg text-white hover:opacity-90">Zurücksetzen</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
